import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Turn } from "../../util/slopifyThread";

interface FailedTurnCardProps {
  turn: Turn;
  onRetry: (turn: Turn) => void;
}

const useCountdown = (target?: number): number => {
  const [remaining, setRemaining] = useState<number>(() =>
    typeof target === "number" ? Math.max(0, Math.ceil((target - Date.now()) / 1000)) : 0,
  );

  useEffect(() => {
    if (typeof target !== "number") return undefined;
    const tick = () => {
      const secs = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setRemaining(secs);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [target]);

  return remaining;
};

const formatCountdown = (seconds: number): string => {
  if (seconds <= 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

export const FailedTurnCard = ({ turn, onRetry }: FailedTurnCardProps) => {
  const { t } = useTranslation();
  const kind = turn.error?.kind ?? "unknown";
  const countdown = useCountdown(turn.error?.retryAfter);
  const retryDisabled = kind === "rate-limit" && countdown > 0;

  const styles: Record<string, string> = {
    "rate-limit": "border-red-200 bg-red-50 text-red-700",
    schema: "border-amber-200 bg-amber-50 text-amber-700",
    network: "border-gray-200 bg-gray-50 text-gray-700",
    auth: "border-amber-200 bg-amber-50 text-amber-700",
    aborted: "border-gray-200 bg-gray-50 text-gray-500",
    unknown: "border-gray-200 bg-gray-50 text-gray-700",
  };

  const labelKey =
    kind === "rate-limit"
      ? "general.assistantScenarIa.errors.rateLimit"
      : kind === "schema"
      ? "general.assistantScenarIa.errors.schema"
      : kind === "aborted"
      ? "general.assistantScenarIa.errors.aborted"
      : kind === "auth"
      ? "general.assistantScenarIa.errors.auth"
      : kind === "network"
      ? "general.assistantScenarIa.errors.network"
      : "general.assistantScenarIa.errors.unknown";

  return (
    <div className={`rounded-md border p-2 mb-2 ${styles[kind]}`}>
      <div className="text-xs font-medium">{t(labelKey)}</div>
      {kind === "rate-limit" && (
        <div className="mt-1 text-[11px]">
          {t("general.assistantScenarIa.errors.rateLimitCountdown", {
            countdown: formatCountdown(countdown),
          })}
        </div>
      )}
      {turn.prompt && (
        <div className="mt-1 text-[11px] italic text-gray-600 truncate">“{turn.prompt}”</div>
      )}
      <button
        type="button"
        className="mt-2 text-[11px] underline disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={retryDisabled}
        onClick={() => onRetry(turn)}
      >
        {t("general.assistantScenarIa.retry")}
      </button>
    </div>
  );
};

export default FailedTurnCard;
